import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { MAX_SAGITTA, MIN_SAGITTA } from '@/editor/constant/wall';
import { ESeparateWallType, SeparateWallData, TArcSeparateWallData } from '@/editor/data/separateWall/SeparateWall';
import { isArcSeparateWallData } from '@/editor/data/separateWall/utils';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { EEntityType, IOperateInfo } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamType,
  EParamUnit,
  EParamsKeys,
  IEditParams
} from '@/editor/types/editParamType';
import { SEPARATE_WALL_NAME_BY_TYPE } from '../const';
import { isDrawSeparateWallMode } from '../utils';

const SEPARATE_WALL_TYPE_OPTIONS = Object.entries(ESeparateWallType)
  .filter(([key]) => key)
  .map(([_, value]) => ({
    label: SEPARATE_WALL_NAME_BY_TYPE[value] || '',
    value: value,
    hide: [ESeparateWallType.BaseVirtualWall, ESeparateWallType.Empty].includes(value)
  }));

function getSeparateGroupItem(separateData: SeparateWallData) {
  const length = Number(Point2dUtils.distanceTo(separateData.start, separateData.end).toFixed(0));
  const values =
    separateData.type === ESeparateWallType.VirtualWall
      ? {
          [EParamsKeys.type]: separateData.type,
          [EParamsKeys.length]: length
        }
      : {
          [EParamsKeys.type]: separateData.type,
          [EParamsKeys.length]: length,
          [EParamsKeys.thickness]: separateData.thickness,
          [EParamsKeys.height]: separateData.height
        };
  const fields: IEditParams['fields'] =
    separateData.type === ESeparateWallType.VirtualWall
      ? {
          [EParamsKeys.type]: {
            type: EParamType.SELECT,
            label: EParamLabel.SEPARATE_WALL_TYPE,
            options: [...SEPARATE_WALL_TYPE_OPTIONS],
            settings: {
              readonly: false
            }
          },
          [EParamsKeys.length]: {
            label: EParamLabel.LENGTH,
            type: EParamType.NUMBER,
            unit: EParamUnit.MM,
            settings: {
              readonly: true,
              max: BUILD_PROPERTIES.MAX_INPUT,
              min: BUILD_PROPERTIES.MIN_INPUT
            }
          }
        }
      : {
          [EParamsKeys.type]: {
            type: EParamType.SELECT,
            label: EParamLabel.SEPARATE_WALL_TYPE,
            options: [...SEPARATE_WALL_TYPE_OPTIONS],
            settings: {
              readonly: false
            }
          },
          [EParamsKeys.thickness]: {
            label: EParamLabel.WALL_THICKNESS,
            type: EParamType.NUMBER,
            unit: EParamUnit.MM,
            options: [...BUILD_PROPERTIES.WALL.THICKNESS_OPTIONS],
            settings: {
              readonly: false,
              max: BUILD_PROPERTIES.MAX_INPUT,
              min: BUILD_PROPERTIES.MIN_INPUT
            }
          },
          [EParamsKeys.height]: {
            label: EParamLabel.WALL_HEIGHT,
            type: EParamType.NUMBER,
            unit: EParamUnit.MM,
            settings: {
              readonly: true,
              max: BUILD_PROPERTIES.MAX_INPUT,
              min: BUILD_PROPERTIES.MIN_INPUT
            }
          },
          [EParamsKeys.length]: {
            label: EParamLabel.LENGTH,
            type: EParamType.NUMBER,
            unit: EParamUnit.MM,
            settings: {
              readonly: true,
              max: BUILD_PROPERTIES.MAX_INPUT,
              min: BUILD_PROPERTIES.MIN_INPUT
            }
          }
        };

  return { fields, values };
}

function getArcSeparateGroupItem(separateData: TArcSeparateWallData) {
  const { start, end, center, clockwise } = separateData;
  const arcSeg = new ArcSeg2d({ start, end, center, clockwise });
  const arcInfo = {
    sagitta: Math.round(Math.abs(arcSeg.getSagitta())),
    chord: Math.round(Point2dUtils.distanceTo(start, end)),
    arcLength: Math.round(arcSeg.length),
    radius: Math.round(arcSeg.radius)
  };

  const typeField: IEditParams['fields'][EParamsKeys.type] = {
    type: EParamType.SELECT,
    label: EParamLabel.SEPARATE_WALL_TYPE,
    options: [...SEPARATE_WALL_TYPE_OPTIONS],
    settings: {
      readonly: false
    }
  };
  const sagittaField: IEditParams['fields'][EParamsKeys.sagitta] = {
    type: EParamType.NUMBER,
    unit: EParamUnit.MM,
    label: EParamLabel.SAGITTA,
    settings: {
      readonly: false,
      max: MAX_SAGITTA,
      min: MIN_SAGITTA
    }
  };
  const chordField: IEditParams['fields'][EParamsKeys.chord] = {
    type: EParamType.NUMBER,
    label: EParamLabel.CHORD,
    unit: EParamUnit.MM,
    settings: {
      readonly: true
    }
  };
  const lengthField: IEditParams['fields'][EParamsKeys.length] = {
    label: EParamLabel.ARC_WALL_LENGTH,
    type: EParamType.NUMBER,
    unit: EParamUnit.MM,
    settings: {
      readonly: true
    }
  };
  const radiusField: IEditParams['fields'][EParamsKeys.radius] = {
    label: EParamLabel.RADIUS,
    type: EParamType.NUMBER,
    unit: EParamUnit.MM,
    settings: {
      readonly: true
    }
  };

  if (separateData.type === ESeparateWallType.VirtualWall) {
    return {
      values: {
        [EParamsKeys.type]: separateData.type,
        [EParamsKeys.sagitta]: arcInfo.sagitta,
        [EParamsKeys.chord]: arcInfo.chord,
        [EParamsKeys.length]: arcInfo.arcLength,
        [EParamsKeys.radius]: arcInfo.radius
      },
      fields: {
        [EParamsKeys.type]: typeField,
        [EParamsKeys.sagitta]: sagittaField,
        [EParamsKeys.chord]: chordField,
        [EParamsKeys.length]: lengthField,
        [EParamsKeys.radius]: radiusField
      }
    };
  }

  return {
    values: {
      [EParamsKeys.type]: separateData.type,
      [EParamsKeys.thickness]: separateData.thickness,
      [EParamsKeys.sagitta]: arcInfo.sagitta,
      [EParamsKeys.chord]: arcInfo.chord,
      [EParamsKeys.length]: arcInfo.arcLength,
      [EParamsKeys.radius]: arcInfo.radius,
      [EParamsKeys.height]: separateData.height
    },
    fields: {
      [EParamsKeys.type]: typeField,
      [EParamsKeys.thickness]: {
        type: EParamType.NUMBER,
        unit: EParamUnit.MM,
        label: EParamLabel.WALL_THICKNESS,
        options: [...BUILD_PROPERTIES.WALL.THICKNESS_OPTIONS],
        settings: {
          readonly: false,
          max: BUILD_PROPERTIES.MAX_INPUT,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.sagitta]: sagittaField,
      [EParamsKeys.chord]: chordField,
      [EParamsKeys.length]: lengthField,
      [EParamsKeys.radius]: radiusField,
      [EParamsKeys.height]: {
        type: EParamType.NUMBER,
        label: EParamLabel.WALL_HEIGHT,
        unit: EParamUnit.MM,
        settings: {
          readonly: true
        }
      }
    }
  };
}

export function getSeparateWallEntityParamInfo(separateId: string) {
  const separateData = globalStores.separateWallStore.getSepData(separateId, false);
  if (separateData) {
    if (isArcSeparateWallData(separateData)) {
      const { fields, values } = getArcSeparateGroupItem(separateData);
      const paramDatas: IEditParams = {
        id: separateData.id,
        type: EEntityType.SeparateWall,
        title: EParamGroupLabel.BORDERCLASS_ARC,
        group:
          separateData.type === ESeparateWallType.VirtualWall
            ? [
                {
                  id: EParamGroupLabel.TYPE,
                  label: EParamGroupLabel.TYPE,
                  fields: [EParamsKeys.type]
                },
                {
                  id: EParamGroupLabel.SIZE,
                  label: EParamGroupLabel.SIZE,
                  fields: [EParamsKeys.sagitta, EParamsKeys.chord, EParamsKeys.length, EParamsKeys.radius]
                }
              ]
            : [
                {
                  id: EParamGroupLabel.TYPE,
                  label: EParamGroupLabel.TYPE,
                  fields: [EParamsKeys.type]
                },
                {
                  id: EParamGroupLabel.SIZE,
                  label: EParamGroupLabel.SIZE,
                  fields: [
                    EParamsKeys.thickness,
                    EParamsKeys.sagitta,
                    EParamsKeys.chord,
                    EParamsKeys.length,
                    EParamsKeys.radius,
                    EParamsKeys.height
                  ]
                }
              ],
        fields,
        values
      };

      return paramDatas;
    }

    const { fields, values } = getSeparateGroupItem(separateData);
    const paramDatas: IEditParams = {
      id: separateData.id,
      type: EEntityType.SeparateWall,
      title: EParamGroupLabel.BORDERCLASS_STRAIGHT,
      group:
        separateData.type === ESeparateWallType.VirtualWall
          ? [
              {
                id: EParamGroupLabel.TYPE,
                label: EParamGroupLabel.TYPE,
                fields: [EParamsKeys.type]
              },
              {
                id: EParamGroupLabel.SIZE,
                label: EParamGroupLabel.SIZE,
                fields: [EParamsKeys.length]
              }
            ]
          : [
              {
                id: EParamGroupLabel.TYPE,
                label: EParamGroupLabel.TYPE,
                fields: [EParamsKeys.type]
              },
              {
                id: EParamGroupLabel.SIZE,
                label: EParamGroupLabel.SIZE,
                fields: [EParamsKeys.thickness, EParamsKeys.height, EParamsKeys.length]
              }
            ],
      fields,
      values
    };

    return paramDatas;
  }

  return undefined;
}

export function getDrawSeparateWallParamInfo(operateInfo: IOperateInfo) {
  if (isDrawSeparateWallMode(operateInfo)) {
    const title = EParamGroupLabel.SEPARATEWALL;

    const paramDatas: IEditParams = {
      // 面板 id 被 ParamsEditor 用作 React key,绘制模式下必须稳定,
      // 否则每次参数变更都会 remount 整个面板,销毁打开中的下拉弹层
      id: operateInfo.mode,
      type: operateInfo.mode,
      title: title,
      group: [
        {
          id: EParamGroupLabel.TYPE,
          label: EParamGroupLabel.TYPE,
          fields: [EParamsKeys.type]
        },
        {
          id: EParamGroupLabel.SIZE,
          label: EParamGroupLabel.SIZE,
          fields: [EParamsKeys.thickness, EParamsKeys.height]
        }
      ],
      values: {
        [EParamsKeys.type]: globalStores.paramStore.separateWallType,
        [EParamsKeys.thickness]: globalStores.paramStore.separateWallThickness,
        [EParamsKeys.layerHeight]: globalStores.paramStore.layerHeight
      },
      fields: {
        [EParamsKeys.type]: {
          type: EParamType.SELECT,
          label: EParamLabel.SEPARATE_WALL_TYPE,
          options: Object.entries(ESeparateWallType)
            .filter(([key]) => key)
            .map(([_, value]) => ({
              label: SEPARATE_WALL_NAME_BY_TYPE[value] || '',
              value: value,
              hide: [
                ESeparateWallType.BaseVirtualWall,
                ESeparateWallType.Empty,
                ESeparateWallType.VirtualWall
              ].includes(value)
            })),
          settings: {
            readonly: false
          }
        },
        [EParamsKeys.thickness]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.WALL_THICKNESS,
          options: [...BUILD_PROPERTIES.WALL.THICKNESS_OPTIONS],
          settings: {
            readonly: false,
            max: BUILD_PROPERTIES.MAX_INPUT,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.layerHeight]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.LAYER_HEIGHT,
          settings: {
            readonly: true
          }
        }
      }
    };

    return paramDatas;
  }

  return undefined;
}
